/**
 * Đặt lịch hộ khách đang gọi điện.
 *
 * Lễ tân đang nghe điện thoại, một tay. Vì vậy:
 *   - Số điện thoại gõ trước, và **tự tìm khách cũ** trong lúc gõ: có khách cũ thì tên tự điền.
 *   - Dịch vụ là danh sách thẻ phẳng (tên / giá / thời gian), không phải `<select>` bị cắt chữ.
 *   - **Đổi ngày ngay trong tờ**: vòng chấm UI 0 bắt tờ cũ báo "đã quá giờ, chọn ngày mai" mà không
 *     có cách nào chọn ngày mai — ngõ cụt.
 *   - Chọn giờ rồi bấm **một** nút chính "Đặt lịch lúc 14:30": đọc lại giờ cho khách trước khi chốt.
 *   - **Nhiều dịch vụ một lần** (gội + cắt, mi + nail): chọn một vẫn là một chạm như cũ; muốn thêm thì
 *     bấm "Thêm dịch vụ". Các dịch vụ làm LIỀN NHAU theo thứ tự đã chọn — máy chủ tìm khe cho cả chuỗi.
 */

import { congVndChuoi, formatDuration, formatVnDate, parseIsoStrict } from '@sapa/shared'
import { Moon, Phone, Plus, Sun, Sunset, UserRound, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  type Api,
  ApiFailure,
  type CustomerHit,
  chuanHoaSdt,
  eachVnDate,
  type GoiYGio,
  gioPhut,
  hienSdt,
  type KtvChon,
  ngayVn,
  type ServiceOption,
  type Slot,
  tenDichVu,
  themNgay,
  tienVnd,
} from './api.ts'
import { useKhoaIdem } from './GoiVi.tsx'
import { nhanNgayNgan } from './ngay.ts'
import { T } from './text/vi.ts'
import { Rong, ThongBao, To, Xuong } from './ui.tsx'

interface Props {
  api: Api
  branchId: string
  tenChiNhanh: string
  date: string
  services: ServiceOption[]
  onClose: () => void
  onBooked: (code: string, ngay: string) => void
  /**
   * Mở từ một ô trống trên lịch: đúng KTV của cột đó, đúng giờ đã chạm. Lễ tân đã TỰ chỉ ra ngày
   * và giờ, nên tờ không tự nhảy sang ngày khác.
   */
  dienSan?: { staffId: string; tenKtv: string; startMin: number }
  /** Số điện thoại điền sẵn (từ trang Khách: "Đặt lịch cho số này"). */
  sdtDau?: string
}

/** Có từ ngần này KTV rảnh cho khe đã chọn thì mới hiện hàng chọn người — ít hơn thì không có gì để chọn. */
const SO_NGUOI_DE_CHON = 2

const NHOM = [
  { ten: T.morning, tu: 0, den: 12 * 60, icon: Sun },
  { ten: T.afternoon, tu: 12 * 60, den: 18 * 60, icon: Sunset },
  { ten: T.evening, tu: 18 * 60, den: 24 * 60, icon: Moon },
]

export function BookSheet({
  api,
  branchId,
  tenChiNhanh,
  date,
  services,
  onClose,
  onBooked,
  dienSan,
  sdtDau,
}: Props) {
  const homNay = ngayVn()
  /** Ngày lúc mở tờ — đổi dịch vụ thì quay về đây trước khi tự tìm lại (vòng 4). */
  const [ngayGoc] = useState(date < homNay ? homNay : date)
  const [ngay, setNgay] = useState(ngayGoc)
  /** Ngày để quay về khi đổi dịch vụ: ngày lúc mở tờ, hoặc ngày MỞ CỬA gần nhất nếu ngày đó tiệm nghỉ (xem effect mở tờ). */
  const ngayBatDau = useRef(ngayGoc)
  /** Khối "Giờ còn trống": sau khi tự nhảy sang ngày mở cửa thì cuộn tới đó (ở 320 / 390 khối này nằm dưới nếp gấp, vòng 69). */
  const khoiGio = useRef<HTMLDivElement | null>(null)
  const daNhayGoc = useRef<{ tu: string; lyDo: string | null } | null>(null)
  /** Số lượt nạp khe: lượt cũ về muộn thì bỏ, không được ghi đè ngày lễ tân vừa chọn (vòng 4). */
  const luot = useRef(0)
  const [sdt, setSdt] = useState(sdtDau ?? '')
  const [ten, setTen] = useState('')
  const [khachCu, setKhachCu] = useState<CustomerHit | null>(null)
  /*
   * KHÔNG chọn sẵn dịch vụ: lễ tân không để ý là đặt nhầm `services[0]`, sai thời lượng, chặn sai lịch
   * của KTV (vòng 2). Danh sách mở sẵn; chọn xong thì thu lại một dòng.
   */
  const [dichVu, setDichVu] = useState<string[]>([])
  /** Khoá ổn định cho effect: mảng mới mỗi lần render không được làm nạp lại khe. */
  const khoaDichVu = dichVu.join(',')
  const [khe, setKhe] = useState<Slot[] | null>(null)
  const [gio, setGio] = useState<number | null>(null)
  /** Tên gọi của KTV có thể chọn, và người lễ tân chọn (`null` = để tiệm xếp). */
  const [dsKtv, setDsKtv] = useState<KtvChon[]>([])
  const [nguoi, setNguoi] = useState<string | null>(null)
  /**
   * MỘT khoá chống lặp cho một lựa chọn (ngày, giờ, dịch vụ, thợ, khách): mạng rớt sau khi máy chủ đã ghi →
   * bấm lại gửi đúng khoá cũ → máy chủ trả lại lịch cũ. Đổi lựa chọn = yêu cầu khác → khoá mới (RETRO 24/09).
   */
  const [khoaDat, lamMoiKhoaDat] = useKhoaIdem()
  const [lyDoTrong, setLyDoTrong] = useState<string | null>(null)
  /** Ngày đang xem là ngày tiệm đóng cửa cả ngày (`CLOSED`): hiện dải cảnh báo có nền, không phải nhãn xám. */
  const [ngayDong, setNgayDong] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  const [goiY, setGoiY] = useState<GoiYGio[]>([])
  const [dangDat, setDangDat] = useState(false)
  /** Danh sách dịch vụ đang mở để: `doi` chọn lại từ đầu, `them` thêm một dịch vụ nữa. */
  const [moDanhSach, setMoDanhSach] = useState<'doi' | 'them' | null>('doi')
  /** Tự tìm ngày gần nhất còn khe khi ngày đang xem kín/hết giờ — tắt khi lễ tân tự chọn ngày. */
  const tuTim = useRef(dienSan === undefined)
  /** Lễ tân đã TỰ chọn ngày: từ đó không bao giờ đổi ngày ngầm nữa (vòng 3: có thể đặt sai ngày). */
  const daTuChon = useRef(dienSan !== undefined)
  const ktv = dienSan?.staffId
  // biome-ignore lint/correctness/useExhaustiveDependencies: chạy lại đúng khi lựa chọn đổi.
  useEffect(() => lamMoiKhoaDat(), [ngay, gio, khoaDichVu, ktv, nguoi, sdt, ten])
  /** Giờ đã chạm trên lịch không còn trống cho KTV đó — nói ra, để lễ tân chọn giờ khác. */
  const [gioChamHet, setGioChamHet] = useState(false)
  /** Vừa tự nhảy ngày: phải NÓI ra, không đổi ngầm. */
  const [daNhay, setDaNhay] = useState<{ tu: string; lyDo: string | null } | null>(null)

  /**
   * KH-GĐ2 4.3.3: khách cũ khớp với số / tên đang gõ (tối đa 3, chỉ khách có số — đặt lịch đi theo số). Hiện NGAY
   * DƯỚI ô đang gõ: chen giữa hai ô thì ô tên bị đẩy xuống, chạm lại ô là chọn nhầm khách (ui-critic v28 BLOCKER).
   */
  const [goiYKhach, setGoiYKhach] = useState<CustomerHit[]>([])
  /** Đã điền "lần trước" cho khách nào (chỉ điền MỘT lần / khách, không đè lựa chọn của lễ tân). */
  const daDienCho = useRef<string | null>(null)
  /** Tóm tắt lần trước đã TỰ điền (một dòng trên thẻ khách quen): ngày dd/MM · dịch vụ · người làm. */
  const [daDienLanTruoc, setDaDienLanTruoc] = useState<{
    ngay: string
    dichVu: string
    nguoi: string | null
  } | null>(null)

  function dienLanTruoc(k: CustomerHit) {
    if (daDienCho.current === k.id) return
    daDienCho.current = k.id
    const lt = k.lanTruoc
    if (lt === null || lt === undefined || dichVu.length > 0) return
    const conBan = lt.variantIds.filter((id) => services.some((s) => s.id === id))
    if (conBan.length === 0) return
    doiDichVu(conBan)
    // Mở từ cột của một KTV thì người đã định sẵn — không đổi. Người lần trước chỉ được giữ nếu còn rảnh ở giờ
    // chọn (`nguoiChon` bên dưới), không thì về "Ai cũng được".
    if (dienSan === undefined && lt.staffId !== null) setNguoi(lt.staffId)
    setDaDienLanTruoc({
      ngay: formatVnDate(parseIsoStrict(lt.ngay)).slice(0, 5),
      dichVu: conBan
        .map((id) => services.find((s) => s.id === id)?.label)
        .filter((x) => x !== undefined)
        .map((x) => tenDichVu(x))
        .join(', '),
      nguoi: lt.staffTen,
    })
  }

  /**
   * "Không phải khách này": bỏ khách đã nhận ra, xoá số + tên để gõ lại, và GỠ LUÔN dịch vụ + người làm đã tự điền
   * theo khách đó — giữ lại là đặt lịch sai dịch vụ, sai thợ cho khách thật (ui-critic v29).
   */
  function boKhachCu() {
    if (daDienLanTruoc !== null) {
      doiDichVu([])
      setNguoi(null)
    }
    setKhachCu(null)
    setSdt('')
    setTen('')
    setDaDienLanTruoc(null)
    daDienCho.current = null
  }

  function chonGoiY(k: CustomerHit) {
    if (k.phone === null) return
    setSdt(hienSdt(k.phone))
    setKhachCu(k)
    setGoiYKhach([])
    dienLanTruoc(k)
  }

  // Gợi ý khi gõ ≥ 3 số (chưa đủ số) hoặc ≥ 2 chữ tên. Chờ 250 ms sau lần gõ cuối.
  const soDaGo = sdt.replace(/\D/g, '')
  const goiYTu: 'so' | 'ten' | null =
    khachCu !== null
      ? null
      : soDaGo.length >= 3 && chuanHoaSdt(sdt) === null
        ? 'so'
        : [...ten.trim()].length >= 2
          ? 'ten'
          : null
  const tuTimKhach = goiYTu === 'so' ? soDaGo : goiYTu === 'ten' ? ten.trim() : null
  useEffect(() => {
    setGoiYKhach([])
    if (tuTimKhach === null) return
    let con = true
    const t = setTimeout(() => {
      void api
        .findCustomer(tuTimKhach)
        .then((r) => {
          if (con) setGoiYKhach(r.customers.filter((k) => k.phone !== null).slice(0, 3))
        })
        .catch(() => {
          /* gợi ý là tiện lợi, lỗi ở đây không được chặn việc đặt lịch */
        })
    }, 250)
    return () => {
      con = false
      clearTimeout(t)
    }
  }, [api, tuTimKhach])

  // Tìm khách cũ khi đã gõ đủ số. Không tìm khi mới gõ 3 số: vừa vô ích vừa ồn.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `dienLanTruoc` đọc lựa chọn MỚI NHẤT và tự chặn lặp theo khách — chạy lại khi số đổi là đủ.
  useEffect(() => {
    const so = sdt.replace(/\D/g, '')
    // Chỉ khi SỐ ĐỦ và đúng dạng: gõ dở 9 số mà tìm là dễ ra khách khác rồi điền nhầm "lần trước" (security 4.3).
    if (chuanHoaSdt(sdt) === null) {
      setKhachCu(null)
      return
    }
    let con = true
    void api
      .findCustomer(so)
      .then((r) => {
        if (!con) return
        const k = r.customers[0] ?? null
        setKhachCu(k)
        if (k !== null) dienLanTruoc(k)
      })
      .catch(() => {
        /* tìm khách là tiện lợi, lỗi ở đây không được chặn việc đặt lịch */
      })
    return () => {
      con = false
    }
  }, [sdt, api])

  // Mở tờ đúng ngày tiệm nghỉ: chip không chọn sẵn một ngày chắc chắn không đặt được — nhảy sang ngày mở kế tiếp trong dải 7 ngày
  // (K2 vòng 66). Chỉ khi lễ tân chưa tự chọn ngày và tờ không mở từ một ô trống của lịch.
  // biome-ignore lint/correctness/useExhaustiveDependencies: chạy MỘT lần lúc mở tờ.
  useEffect(() => {
    if (dienSan !== undefined) return
    let con = true
    void (async () => {
      try {
        if ((await api.day(branchId, ngayGoc)).dongCua == null) return
        const cuoiDai = themNgay(homNay, 6)
        for (const d of eachVnDate(themNgay(ngayGoc, 1), 6)) {
          if (d > cuoiDai) return
          const x = await api.day(branchId, d)
          if (!con || daTuChon.current) return
          if (x.dongCua == null) {
            ngayBatDau.current = d
            daNhayGoc.current = { tu: ngayGoc, lyDo: 'CLOSED' }
            setNgay(d)
            setDaNhay(daNhayGoc.current)
            return
          }
        }
      } catch {
        /* đoán ngày là tiện lợi: lỗi mạng ở đây không được chặn việc đặt lịch */
      }
    })()
    return () => {
      con = false
    }
  }, [])

  useEffect(() => {
    if (daNhay !== null && khe !== null && khe.length > 0) {
      khoiGio.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    }
  }, [daNhay, khe])

  const napKhe = useCallback(async () => {
    if (khoaDichVu === '') return
    const toi = ++luot.current
    const cu = () => toi !== luot.current
    setKhe(null)
    setGio(null)
    setLyDoTrong(null)
    setNgayDong(false)
    try {
      const r = await api.slots(branchId, ngay, khoaDichVu, ktv)
      if (cu()) return
      if (r.slots.length === 0 && tuTim.current) {
        // Như Mini App: 21 giờ mở tờ thì nhảy sang ngày mai, không bắt lễ tân nhìn một thẻ trống.
        // Chỉ tìm trong 7 ngày của dải chip, để ngày được chọn luôn nhìn thấy được (vòng 3).
        const cuoiDai = themNgay(homNay, 6)
        for (const d of eachVnDate(themNgay(ngay, 1), 6)) {
          if (d > cuoiDai) break
          const r2 = await api.slots(branchId, d, khoaDichVu, ktv)
          if (cu() || !tuTim.current) return
          if (r2.slots.length > 0) {
            tuTim.current = false
            setDaNhay({ tu: ngay, lyDo: r.reason ?? null })
            setNgay(d)
            return
          }
        }
      }
      tuTim.current = false
      setDsKtv(r.ktv ?? [])
      setKhe(r.slots)
      if (dienSan !== undefined && ngay === date) {
        const co = r.slots.some((x) => x.startMin === dienSan.startMin)
        setGioChamHet(!co)
        if (co) setGio(dienSan.startMin)
      } else setGioChamHet(false)
      setNgayDong(r.slots.length === 0 && r.reason === 'CLOSED')
      // Ngày tiệm đóng cửa (CLOSED): máy chủ nói "Hôm nay…" (chữ của Mini App) — ở quầy ngày đang chọn có thể là ngày khác, nói thẳng "Tiệm nghỉ ngày này".
      setLyDoTrong(
        r.slots.length === 0
          ? r.reason === 'CLOSED'
            ? T.bookClosedDay
            : (r.message ?? T.noSlot)
          : null,
      )
    } catch (e) {
      if (cu()) return
      setKhe([])
      setLyDoTrong(e instanceof ApiFailure ? e.messageVi : T.noSlot)
    }
  }, [api, branchId, ngay, khoaDichVu, homNay, ktv, dienSan, date])

  useEffect(() => {
    void napKhe()
  }, [napKhe])

  async function dat(startMin: number) {
    const so = sdt.trim() === '' ? null : chuanHoaSdt(sdt)
    if (sdt.trim() !== '' && so === null) {
      setLoi(T.phoneInvalid)
      return
    }
    setDangDat(true)
    setLoi(null)
    try {
      const tenGui = khachCu?.fullName ?? (ten.trim() === '' ? T.namePlaceholder : ten.trim())
      const kq = await api.book(
        {
          branchId,
          date: ngay,
          startMin,
          variantIds: dichVu,
          ...(ktv !== undefined
            ? { staffId: ktv }
            : nguoiChon !== null
              ? { staffId: nguoiChon }
              : {}),
          fullName: tenGui,
          ...(so === null ? {} : { phone: so }),
        },
        khoaDat,
      )
      onBooked(kq.code, ngay)
    } catch (e) {
      if (e instanceof ApiFailure) {
        setLoi(e.messageVi)
        setGoiY(e.goiY)
        // Khe vừa bị người khác lấy: lưới giờ đang hiện VẪN còn khe đó. Không nạp lại thì lễ tân
        // bấm đúng khe cũ lần nữa và lại nhận cùng một lỗi.
        if (e.code === 'SLOT_TAKEN' || e.code === 'SLOT_NOT_AVAILABLE') void napKhe()
      } else {
        setLoi(T.bookFailed)
      }
    } finally {
      setDangDat(false)
    }
  }

  const kheChon = gio === null ? undefined : khe?.find((s) => s.startMin === gio)
  /** KTV chọn được cho khe đang chọn. Mở từ cột của một KTV thì người đã định sẵn — không hỏi lại. */
  const chonDuoc =
    dienSan === undefined && kheChon !== undefined
      ? dsKtv.filter((k) => kheChon.staffChoiceIds.includes(k.id))
      : []
  /**
   * Người đã chọn vẫn rảnh ở giờ mới thì GIỮ (kể cả khi chỉ còn mình người đó); không còn rảnh thì
   * quay về "Ai cũng được" — không bao giờ gửi người cũ.
   */
  const nguoiChon = chonDuoc.some((k) => k.id === nguoi) ? nguoi : null
  const hienChonNguoi = chonDuoc.length >= SO_NGUOI_DE_CHON || nguoiChon !== null

  const daChon = dichVu.flatMap((id) => services.filter((s) => s.id === id))
  const tongPhut = daChon.reduce((t, s) => t + s.durationMin, 0)

  function doiDichVu(moi: string[]) {
    // Đổi dịch vụ mà lễ tân chưa tự chọn ngày: quay về ngày ban đầu rồi tìm lại — ngày "kín" của
    // dịch vụ 2 giờ có thể còn chỗ cho dịch vụ 45 phút (vòng 4).
    // Hôm nay đóng cửa mà đã nhảy sang ngày mở: quay về NGÀY ĐÃ NHẢY (không về hôm nay rồi nhảy lại và in "đã kín", vòng 67).
    if (!daTuChon.current) {
      tuTim.current = true
      setDaNhay(daNhayGoc.current)
      setNgay(ngayBatDau.current)
    }
    setDichVu(moi)
    setMoDanhSach(moi.length === 0 ? 'doi' : null)
  }

  const cacNgay = Array.from({ length: 7 }, (_, i) => themNgay(homNay, i))
  /** Một tên cho một ngày ở mọi chỗ trong tờ — chip và hộp báo không được gọi khác nhau (vòng 4). */
  const nhanNgay = (d: string) =>
    d === homNay ? T.today : d === themNgay(homNay, 1) ? T.tomorrowShort : nhanNgayNgan(d)
  const nhanChip = (d: string, _i: number) => nhanNgay(d)

  return (
    <To nhan={T.newBooking} onDong={onClose}>
      <header className="to-dau">
        <div style={{ flex: 1 }}>
          <h2>{T.newBooking}</h2>
          <p className="chu-phu">{tenChiNhanh}</p>
        </div>
        <button
          type="button"
          className="nut nut-nhat nut-icon"
          aria-label={T.close}
          onClick={onClose}
        >
          <X size={22} />
        </button>
      </header>

      <div className="to-than">
        <div className="muc-to">
          <h3>{T.customerSection}</h3>
          <div className="truong">
            <label className="nhan" htmlFor="sdt-dat">
              {T.phoneLabel}
            </label>
            <div className="o-nhap-icon">
              <Phone size={20} />
              <input
                id="sdt-dat"
                className="o-nhap"
                type="tel"
                inputMode="tel"
                autoComplete="off"
                placeholder={T.phonePlaceholder}
                value={sdt}
                onChange={(e) => setSdt(e.target.value)}
              />
            </div>
          </div>
          {goiYKhach.length > 0 && goiYTu === 'so' && (
            <div className="goi-y-khach">
              <span className="nhan">{T.suggestTitle}</span>
              <div className="ds-chon">
                {goiYKhach.map((k) => (
                  <button key={k.id} type="button" className="muc-chon" onClick={() => chonGoiY(k)}>
                    <UserRound size={20} />
                    <span className="chu">
                      <b>{k.fullName}</b>
                      <span>
                        {hienSdt(k.phone)}
                        {k.lanTruoc !== null && k.lanTruoc !== undefined && (
                          <>
                            {' · '}
                            {k.lanTruoc.variantIds
                              .map((id) => services.find((s) => s.id === id)?.label)
                              .filter((x) => x !== undefined)
                              .map((x) => tenDichVu(x))
                              .join(', ')}
                          </>
                        )}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {khachCu !== null ? (
            <div className="the-khach-cu" style={{ marginTop: 12 }}>
              <UserRound size={20} />
              <div>
                <b>{khachCu.fullName}</b>
                <span className="dong-khach-quen">
                  {daDienLanTruoc === null
                    ? T.returning
                    : T.returningLine(
                        daDienLanTruoc.ngay,
                        daDienLanTruoc.dichVu,
                        daDienLanTruoc.nguoi,
                      )}
                </span>
                <button type="button" className="nut nut-nhat nut-bo-khach" onClick={boKhachCu}>
                  {T.notThisCustomer}
                </button>
              </div>
              {khachCu.canhBao !== null && (
                <span className="nhan-nho canh-bao" style={{ marginLeft: 'auto' }}>
                  {T.noShowWarning}
                </span>
              )}
            </div>
          ) : (
            <div className="truong" style={{ marginTop: 12 }}>
              <label className="nhan" htmlFor="ten-dat">
                {T.nameLabel} <span className="nhan-phu">· {T.optional}</span>
              </label>
              <input
                id="ten-dat"
                className="o-nhap"
                placeholder={T.nameExample}
                value={ten}
                onChange={(e) => setTen(e.target.value)}
              />
            </div>
          )}
          {goiYKhach.length > 0 && goiYTu === 'ten' && (
            <div className="goi-y-khach">
              <span className="nhan">{T.suggestTitle}</span>
              <div className="ds-chon">
                {goiYKhach.map((k) => (
                  <button key={k.id} type="button" className="muc-chon" onClick={() => chonGoiY(k)}>
                    <UserRound size={20} />
                    <span className="chu">
                      <b>{k.fullName}</b>
                      <span>
                        {hienSdt(k.phone)}
                        {k.lanTruoc !== null && k.lanTruoc !== undefined && (
                          <>
                            {' · '}
                            {k.lanTruoc.variantIds
                              .map((id) => services.find((s) => s.id === id)?.label)
                              .filter((x) => x !== undefined)
                              .map((x) => tenDichVu(x))
                              .join(', ')}
                          </>
                        )}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="muc-to">
          <h3>{T.serviceSection}</h3>
          {/* Thu gọn còn MỘT dòng khi đã chọn: danh sách 14 dịch vụ từng đẩy phần Giờ ra khỏi màn đầu. */}
          {moDanhSach === 'doi' ? (
            <div className="ds-chon">
              {services.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  aria-pressed={dichVu.includes(s.id)}
                  className={`muc-chon ${dichVu.includes(s.id) ? 'dang-chon' : ''}`}
                  onClick={() => doiDichVu([s.id])}
                >
                  <span className="cham-chon" />
                  <span className="chu">
                    <b>{tenDichVu(s.label)}</b>
                    <span>{formatDuration(s.durationMin)}</span>
                  </span>
                  <span className="gia">{tienVnd(s.priceVnd)}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="ds-chon">
              {/* Dịch vụ đã chọn LUÔN hiện, kể cả lúc đang thêm — lễ tân phải thấy mình thêm vào đâu (vòng 7). */}
              {daChon.length === 1 && daChon[0] !== undefined && moDanhSach === null ? (
                <button
                  type="button"
                  aria-pressed
                  className="muc-chon dang-chon"
                  onClick={() => setMoDanhSach('doi')}
                >
                  <span className="cham-chon" />
                  {/* Thời lượng + giá chung MỘT dòng dưới tên (vòng 44): ba cột cạnh nhau ở 360px bẻ tên 2 dòng + thời lượng = 3 dòng. */}
                  <span className="chu">
                    <b>{tenDichVu(daChon[0].label)}</b>
                    <span>
                      {formatDuration(daChon[0].durationMin)} ·{' '}
                      <span className="gia">{tienVnd(daChon[0].priceVnd)}</span>
                    </span>
                  </span>
                  <span className="doi-chon">{T.change}</span>
                </button>
              ) : (
                daChon.map((s, i) => (
                  <div key={s.id} className="muc-chon dang-chon muc-chon-nhieu">
                    <span className="so-thu-tu">{i + 1}</span>
                    <span className="chu">
                      <b>{tenDichVu(s.label)}</b>
                      <span>{formatDuration(s.durationMin)}</span>
                    </span>
                    <span className="gia">{tienVnd(s.priceVnd)}</span>
                    {daChon.length > 1 && (
                      <button
                        type="button"
                        className="nut nut-nhat nut-icon bo-dv"
                        aria-label={T.removeService(tenDichVu(s.label))}
                        onClick={() => doiDichVu(dichVu.filter((x) => x !== s.id))}
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                ))
              )}
              {daChon.length > 1 && (
                <p className="tong-dv">
                  <span>{T.servicesTotal(daChon.length, formatDuration(tongPhut))}</span>
                  <b>{tienVnd(congVndChuoi(daChon.map((s) => s.priceVnd)))}</b>
                </p>
              )}
              {moDanhSach === 'them' ? (
                <>
                  <div className="dau-them-dv">
                    <span>{T.addServiceHint}</span>
                    <button
                      type="button"
                      className="nut nut-nhat nut-nho"
                      onClick={() => setMoDanhSach(null)}
                    >
                      {T.cancelAddService}
                    </button>
                  </div>
                  {services
                    .filter((s) => !dichVu.includes(s.id))
                    .map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="muc-chon"
                        onClick={() => doiDichVu([...dichVu, s.id])}
                      >
                        <Plus size={20} className="icon-them" />
                        <span className="chu">
                          <b>{tenDichVu(s.label)}</b>
                          <span>{formatDuration(s.durationMin)}</span>
                        </span>
                        <span className="gia">{tienVnd(s.priceVnd)}</span>
                      </button>
                    ))}
                </>
              ) : (
                dichVu.length < 5 &&
                dichVu.length < services.length && (
                  <button
                    type="button"
                    className="nut nut-vien them-dv"
                    onClick={() => setMoDanhSach('them')}
                  >
                    <Plus size={18} />
                    {T.addService}
                  </button>
                )
              )}
            </div>
          )}
        </div>

        <div className="muc-to">
          <h3>{T.bookingFor}</h3>
          <div className="hang-chip cuon">
            {cacNgay.map((d, i) => (
              <button
                key={d}
                type="button"
                className={`chip ${d === ngay ? 'dang-chon' : ''} ${d === homNay && daNhay?.lyDo === 'TOO_LATE' ? 'mo-nhat' : ''}`}
                onClick={() => {
                  tuTim.current = false
                  daTuChon.current = true
                  setDaNhay(null)
                  setNgay(d)
                }}
              >
                {nhanChip(d, i)}
              </button>
            ))}
          </div>
        </div>

        <div className="muc-to" ref={khoiGio}>
          <h3>{T.timeSection}</h3>
          {dienSan !== undefined && (
            <p className="chu-phu" style={{ marginBottom: 12 }}>
              {T.forStaff(dienSan.tenKtv)}
            </p>
          )}
          {gioChamHet && dienSan !== undefined && khe !== null && khe.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <ThongBao loai="canh-bao">{T.tappedTimeTaken(gioPhut(dienSan.startMin))}</ThongBao>
            </div>
          )}
          {daNhay !== null && khe !== null && khe.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <ThongBao loai="canh-bao">
                {T.jumpedDay(nhanNgay(daNhay.tu), nhanNgay(ngay), daNhay.lyDo)}
              </ThongBao>
            </div>
          )}
          {loi !== null && (
            <div style={{ marginBottom: 12 }}>
              <ThongBao loai="loi">
                {loi}
                {goiY.length > 0 && <div style={{ marginTop: 4 }}>{T.slotTakenHint}</div>}
              </ThongBao>
              {goiY.length > 0 && (
                <div className="luoi-khe" style={{ marginTop: 8 }}>
                  {goiY.map((g) => (
                    <button
                      key={g.startMin}
                      type="button"
                      className="khe"
                      onClick={() => setGio(g.startMin)}
                    >
                      {gioPhut(g.startMin)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {dichVu.length === 0 ? (
            <p className="chu-phu">{T.pickServiceFirst}</p>
          ) : khe === null ? (
            <div className="luoi-khe">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                <Xuong key={i} cao={44} />
              ))}
            </div>
          ) : khe.length === 0 && ngayDong ? (
            <>
              <ThongBao loai="canh-bao">{T.bookClosedDay}</ThongBao>
              <p className="chu-phu" style={{ marginTop: 8 }}>
                {T.tryOtherDay}
              </p>
            </>
          ) : khe.length === 0 ? (
            <Rong icon={<Moon size={26} />} tieuDe={lyDoTrong ?? T.noSlot} moTa={T.tryOtherDay} />
          ) : (
            NHOM.map((n) => {
              const cacKhe = khe.filter((s) => s.startMin >= n.tu && s.startMin < n.den)
              if (cacKhe.length === 0) return null
              return (
                <div key={n.ten} className="nhom-khe">
                  <h4>
                    <n.icon size={16} />
                    {n.ten}
                  </h4>
                  <div className="luoi-khe">
                    {cacKhe.map((s) => (
                      <button
                        key={s.startMin}
                        type="button"
                        className={`khe ${gio === s.startMin ? 'dang-chon' : ''}`}
                        aria-pressed={gio === s.startMin}
                        onClick={() => setGio(s.startMin)}
                      >
                        {gioPhut(s.startMin)}
                      </button>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Chọn người nằm NGAY TRÊN nút chốt, luôn nhìn thấy: lưới giờ dài, đặt ở cuối lưới thì lễ tân
          chọn giờ xong không thấy nó (chấm bằng mắt 23/09). */}
      {hienChonNguoi && (
        <div className="to-chon-ktv">
          <span className="nhan-ktv">{T.staffPickTitle}</span>
          <div className="hang-chip cuon">
            <button
              type="button"
              className={`chip ${nguoiChon === null ? 'dang-chon' : ''}`}
              aria-pressed={nguoiChon === null}
              onClick={() => setNguoi(null)}
            >
              {T.staffAny}
            </button>
            {chonDuoc.map((k) => (
              <button
                key={k.id}
                type="button"
                className={`chip ${nguoiChon === k.id ? 'dang-chon' : ''}`}
                aria-pressed={nguoiChon === k.id}
                onClick={() => setNguoi(k.id)}
              >
                {k.ten}
              </button>
            ))}
          </div>
        </div>
      )}
      <footer className="to-chan">
        <button
          type="button"
          className="nut nut-chinh nut-lon"
          disabled={gio === null || dangDat || dichVu.length === 0}
          onClick={() => {
            if (gio !== null) void dat(gio)
          }}
        >
          {dangDat
            ? T.bookingNow
            : dichVu.length === 0
              ? T.pickServiceBtn
              : gio === null
                ? T.pickTimeFirst
                : T.bookAt(gioPhut(gio), nhanNgay(ngay))}
        </button>
      </footer>
    </To>
  )
}
